import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { Button } from "./ui/button";
import { formatCOP } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_MS = 3000;
const POLL_MAX = 20; // ~60s

export const PaymentResult = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [phase, setPhase] = useState(orderId ? "loading" : "error");
  // loading | success | pending | failed | error
  const polls = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let timer;

    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        if (["paid", "paid_mock"].includes(data.payment_status)) {
          setPhase("success");
        } else if (data.payment_status === "pending") {
          polls.current += 1;
          if (polls.current >= POLL_MAX) {
            setPhase("pending");
          } else {
            setPhase("loading");
            timer = setTimeout(fetchOrder, POLL_MS);
          }
        } else {
          setPhase("failed");
        }
      } catch {
        if (!cancelled) setPhase("error");
      }
    };

    fetchOrder();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  const retry = () => {
    polls.current = 0;
    setPhase("loading");
    // Re-dispara el efecto cambiando la key del polling vía recarga simple.
    window.location.reload();
  };

  const card = () => {
    switch (phase) {
      case "loading":
        return (
          <>
            <Loader2 className="h-12 w-12 text-[#E85A3C] animate-spin mb-5" />
            <h1 className="font-serif text-3xl text-slate-900">
              Confirmando tu pago…
            </h1>
            <p className="mt-3 text-slate-600 max-w-md">
              Estamos verificando la respuesta de la pasarela. Esto toma solo
              unos segundos.
            </p>
          </>
        );
      case "success":
        return (
          <>
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-9 w-9 text-green-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">
              ¡Pago confirmado!
            </h1>
            <p className="mt-3 text-slate-600 max-w-md">
              Tu pedido{" "}
              <span
                data-testid="payment-result-order-number"
                className="font-mono font-semibold text-slate-900"
              >
                #{order.order_number}
              </span>{" "}
              por <strong>{formatCOP(order.total)}</strong> quedó registrado.
              Te enviamos el resumen a{" "}
              <span className="font-medium">{order.customer.email}</span>.
            </p>
          </>
        );
      case "pending":
        return (
          <>
            <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
              <Clock className="h-9 w-9 text-amber-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">
              Estamos confirmando tu pago
            </h1>
            <p className="mt-3 text-slate-600 max-w-md">
              La pasarela aún no nos confirma el pago de tu pedido
              {order ? (
                <>
                  {" "}
                  <span className="font-mono font-semibold text-slate-900">
                    #{order.order_number}
                  </span>
                </>
              ) : null}
              . Puede tardar unos minutos; te avisaremos por email.
            </p>
            <Button
              onClick={retry}
              data-testid="payment-result-refresh"
              variant="outline"
              className="mt-6 rounded-full border-slate-900 text-slate-900"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Volver a verificar
            </Button>
          </>
        );
      case "failed":
        return (
          <>
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
              <XCircle className="h-9 w-9 text-red-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">
              El pago no se completó
            </h1>
            <p className="mt-3 text-slate-600 max-w-md">
              La pasarela reportó el pago como{" "}
              <span className="font-medium">{order?.payment_status}</span>. No
              se hizo ningún cobro definitivo; puedes intentarlo de nuevo desde
              la tienda.
            </p>
          </>
        );
      default:
        return (
          <>
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
              <XCircle className="h-9 w-9 text-red-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">
              No encontramos tu pedido
            </h1>
            <p className="mt-3 text-slate-600 max-w-md">
              El enlace no incluye un pedido válido. Si pagaste y ves este
              mensaje, escríbenos con tu comprobante a hola@acuadros.co.
            </p>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased flex flex-col">
      <Navbar />
      <main
        data-testid="payment-result-page"
        className="flex-1 flex items-center justify-center pt-28 pb-16 px-6"
      >
        <div
          data-testid="payment-result-status"
          data-status={phase}
          className="flex flex-col items-center text-center bg-white border border-slate-200 rounded-2xl shadow-sm px-8 md:px-14 py-12 max-w-xl"
        >
          {card()}
          <Link
            to="/"
            data-testid="payment-result-home-link"
            className="mt-8 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-7 py-3 rounded-full font-medium text-sm transition-colors"
          >
            Volver a la tienda
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};
